import React from 'react';
import { BillingPlanRepository, SubscriptionRepository } from '../../../app/billing';
import { SubscriptionAddonController } from './subscriptionAddonInterface';

export const createSubscriptionAddonController = ({
    pluginName,
    subscriptions: subsRepo,
    plans: plansRepo,
}: {
    pluginName: string;
    subscriptions: SubscriptionRepository;
    plans: BillingPlanRepository;
}): SubscriptionAddonController => {
    return {
        useProps(context, data) {
            const subscription = subsRepo.useLookup(context, {});
            const plans = plansRepo.useList(context, { version: 2 }, {});
            const plugin = React.useMemo(() => {
                if (plans.data && subscription.data) {
                    const plan = plans.data.items.find(
                        (x) => subscription.data?.plan === x.id
                    );
                    if (plan) {
                        const pluginFound = plan.add_ons.find((x) => x.id === pluginName);
                        if (pluginFound) {
                            return {
                                ...pluginFound,
                                isInstalled: subscription.data.add_ons.some(
                                    (x) => pluginFound.id === x
                                ),
                            };
                        } else {
                            return 'no_plugin';
                        }
                    } else {
                        return 'no_plan';
                    }
                } else {
                    return 'no_plan';
                }
            }, [plans.data, subscription.data]);
            const updateSubscription = subsRepo.useUpdate(context);
            const createPortal = subsRepo.usePortal(context);

            return {
                plugin,
                group: data.group,
                saveState: {
                    isError: updateSubscription.isError,
                    isLoading: updateSubscription.isLoading,
                    error: updateSubscription.error,
                    shouldCreatePaymentMethod:
                        updateSubscription.error?.message.includes(
                            'payment method is required'
                        ) || false,
                },
                createPaymentMethod: {
                    isError: createPortal.isError,
                    isLoading: createPortal.isLoading,
                    portal: createPortal.data,
                    mutate: () => {
                        createPortal
                            .mutateAsync({
                                return_path: window.location.pathname,
                                payment_methods: true,
                            })
                            .then((x) => {
                                document.location.href = x.url;
                            });
                    },
                },
                onSubscribe: () => {
                    if (!(typeof plugin === 'string') && subscription.data) {
                        if (plugin.isInstalled) {
                            return Promise.reject('plugin already installed');
                        } else {
                            return updateSubscription.mutateAsync({
                                add_ons: Array.from(
                                    new Set<string>([
                                        ...subscription.data.add_ons,
                                        plugin.id,
                                    ])
                                ),
                                subscriptionId: subscription.data.id,
                                options: {
                                    wait: 60000,
                                    idempotency_key: `subscription_${subscription.data.id}_${plugin.id}_${Date.now().toString()}`,
                                },
                            });
                        }
                    } else {
                        return Promise.reject('plugin not found');
                    }
                },
                onUnSubscribe: () => {
                    if (!(typeof plugin === 'string') && subscription.data) {
                        return updateSubscription.mutateAsync({
                            add_ons: subscription.data.add_ons.filter(
                                (x) => x !== plugin.id
                            ),
                            subscriptionId: subscription.data.id,
                            options: {
                                wait: 60000,
                                idempotency_key: `subscription_${subscription.data.id}_${plugin.id}_${Date.now().toString()}`,
                            },
                        });
                    } else {
                        return Promise.reject('plugin not found');
                    }
                },
            };
        },
    };
};
